import React, {useRef} from 'react'
import {Button, Dialog, DialogContent, IconButton} from "@mui/material";
import {observer} from "mobx-react";
import CloseIcon from '@mui/icons-material/Close';
import './modal.scss';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {ProcedureRequester} from "../../MobxStores/RequestApi/ProcedureRequester";
import {useBackDrop, useStores} from "../../Hooks";
import EllipsisContainer from "../Shared/EllipsisContainer";
import sharedUtils from "../../serverClientShared/sharedUtils";
import {fileTypeEnum} from "../../serverClientShared/sharedEnums";
import {DATA_INTERVAL} from "../../MobxStores/DataStore";
import ConditionalComponent from "../Shared/ConditionalComponent";

export const SelectFusionContent = observer(({isSitePrepTemplate}) => {

    const {modalStore, procedureStore, generalStore, projectStore, dataStore, dragStore} = useStores();
    const dropRef = useRef(null);
    useBackDrop(
        dropRef,
        () => dragStore.isDraggingFiles = true,
        () => dragStore.isDraggingFiles = false,
        saveFusionsByDrag,
    );

    const procedure = !isSitePrepTemplate ? procedureStore.currentProcedure : modalStore.modalData.sitePrepData || {};

    async function saveFusionsByDrag(files) {
        const fusionFilesOnly = [];
        for (const file of files) {
            const fileType = sharedUtils.getTypeFromFileName(file.name);
            if (sharedUtils.fusionTypes.includes(fileType) && fileType !== fileTypeEnum.FUSION) {
                fusionFilesOnly.push(file);
            }
        }
        if (!fusionFilesOnly.length) {
            modalStore.showErrorMessage(generalStore.translate('noMatchingFilesFound'));
            return;
        }
        const fileSchemas = await generalStore.uploadFiles(fusionFilesOnly, procedure.ProjectRef);

        await addMultipleFusionFiles(fileSchemas, generalStore.translate('cannotUpdateModels'));
    }

    const addMultipleFusionFiles = async (files, errorMessage) => {

        files.forEach(file => {
            switch (file.FileType) {
                case fileTypeEnum.FRONTLINE_MAC:
                    procedure.ThreeDFilesOsx = file;
                    break;

                case fileTypeEnum.FRONTLINE_WEB:
                    procedure.ThreeDFilesWebgl = file;
                    break;

                case fileTypeEnum.FRONTLINE_WINDOWS:
                    procedure.ThreeDFilesWin = file;
                    break;

                case fileTypeEnum.FRONTLINE_ANDROID:
                    procedure.ThreeDFilesAndroid = file;
                    break;

                case fileTypeEnum.FRONTLINE_HOLOLENS:
                    procedure.ThreeDFilesHololens = file;
                    break;

                case fileTypeEnum.FRONTLINE_IOS:
                    procedure.ThreeDFilesIOs = file;
                    break;

                default:
                    console.error("cannot id this file", file);
                    return;
            }
        });

        await updateProcedure(errorMessage);
    }

    async function updateProcedure(errorMessage) {
        if (!isSitePrepTemplate) {
            const promise = async () => {
                procedureStore.currentProcedure = await ProcedureRequester.createOrUpdateProcedure(procedure, generalStore.workspace);
                const projectId = projectStore.currentProject?.ProjectIdentifier;
                const projectProcedureMap = dataStore.projectsProceduresMap.get(projectId) || new Map();
                projectProcedureMap.set(procedure.ProcedureIdentifier, procedureStore.currentProcedure);
                dataStore.set('projectsProceduresMap', projectId, projectProcedureMap);
                dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
            }
            const loadingMessage = generalStore.translate('updatingModel') + '...';
            const successMessage = generalStore.translate('modelUpdated');
            await modalStore.runAsync(promise(), errorMessage, successMessage, loadingMessage);
        } else {
            modalStore.modalData.sitePrepData = {...modalStore.modalData.sitePrepData, ...procedure};
            modalStore.showInfoMessage(generalStore.translate('successfullyUpdatedFile'));
        }
    }

    async function saveFusionFile(filePropertyName, newValue, errorMessage) {
        procedure[filePropertyName] = newValue;
        await updateProcedure(errorMessage);
    }

    const checkNoDuplicateFileTypes = files => {
        let isDuplicated = false;
        const fileMap = new Map(Object.values(fileTypeEnum).map(fileType => [fileType, 0]));
        files.forEach(file => {
            const fileTypeNum = fileMap.get(file.FileType) || 0;
            if(!!fileTypeNum) {
                isDuplicated = true;
            }
            fileMap.set(file.FileType, fileTypeNum + 1);
        });
        return isDuplicated;
    }

    function renderFusionInput(systemFriendlyName, filePropertyName) {
        const fileName = procedure[filePropertyName] ? procedure[filePropertyName].FileName : '---';
        return (
            <div className='fusion-input'>
                <div className='fusion-input-buttons'>
                    <div className='fusion-input-title'>
                        {systemFriendlyName}
                    </div>
                    <div>
                        <Button variant='outlined' onClick={() => {
                            const mediaSelectionData = {
                                onMediaSelected: async (files) => {
                                    if(checkNoDuplicateFileTypes(files)) {
                                        return modalStore.showErrorMessage(generalStore.translate('pleaseSelectOnlyOneFileOfEachFusionType'));
                                    }
                                    await addMultipleFusionFiles(files, generalStore.translate('cannotSaveFusionFile'));
                                },
                                onlyFusion: true
                            };
                            if (!isSitePrepTemplate) {
                                modalStore.modalData = mediaSelectionData;
                            } else modalStore.modalData = {...modalStore.modalData, ...mediaSelectionData};
                            modalStore.isMediaLibraryModalOpen = true;
                        }}>
                            <span
                                className='hide-in-mobile'> <OpenInNewIcon/>&nbsp;</span> {procedure[filePropertyName] ? generalStore.translate('replaceFile') : generalStore.translate('selectFile')}
                        </Button>
                    </div>
                    <div>
                        <IconButton
                            onClick={() => saveFusionFile(filePropertyName, null, generalStore.translate('cannotDeleteFusionFile'))}
                            disabled={!procedure[filePropertyName]}
                        >
                            <DeleteForeverIcon/>
                        </IconButton>
                    </div>
                </div>
                <EllipsisContainer value={fileName}>
                    <div className='fusion-name'>
                        {fileName}
                    </div>
                </EllipsisContainer>
            </div>
        )
    }

    return (
        <div className='select-fusion-container' ref={dropRef}>
            <ConditionalComponent if={!isSitePrepTemplate}>
                <div className='select-fusion-header'>
                    {generalStore.translate('frontline3DFileSettings')}
                </div>
            </ConditionalComponent>
            <div className='select-fusion-inputs'>
                {renderFusionInput('Windows (desktop)', 'ThreeDFilesWin')}
                {renderFusionInput('Android', 'ThreeDFilesAndroid')}
                {renderFusionInput('HoloLens', 'ThreeDFilesHololens')}
                {renderFusionInput('iOS', 'ThreeDFilesIOs')}
                {renderFusionInput('WEBGL', 'ThreeDFilesWebgl')}
                {renderFusionInput('OSX', 'ThreeDFilesOsx')}
            </div>
        </div>
    )

});
const SelectFusionDialog = observer(() => {
    const {modalStore, dragStore} = useStores();

    function closeModal() {
        modalStore.modalData = {};
        modalStore.isSelectFusionDialogOpen = false;
    }


    return (
        <Dialog
            className={'dialog big-dialog select-fusion-dialog' + (dragStore.isDraggingFiles ? ' dragging' : '')}
            open={modalStore.isSelectFusionDialogOpen}
            onClose={closeModal}
            fullWidth
            maxWidth='md'
        >
            <DialogContent>
                <Button
                    disableElevation
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    className='close-dialog'
                    onClick={closeModal}
                >
                    <CloseIcon/>
                </Button>
                <SelectFusionContent/>
            </DialogContent>
        </Dialog>
    )
});

export default SelectFusionDialog;
